import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { RightSideDrawerService } from '../shared/right-side-drawer/right-side-drawer.service';
import { SessionService } from '../shared/session-service/session.service';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { role } from '../shared/constants/role-enum';
import { TopNavigationService } from 'src/app/shared/navigation/top-navbar/top-nav.service';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { LoadingIndicatorService, ToastrService, WindowRef } from '@kpmg-tax/ui';
import { HomeEditAnEngagementService } from '../shared/right-side-drawer/home-edit-an-engagement/home-edit-an-engagement.service';
import { HomeService } from './home.service';
import { getEngagementFlagById, getEngagementFlagStyleById } from '../shared/lib/custom-tools';
import { ClientInfoDto } from '../shared/models/engagement/client-info-dto';
import { EngagementInfoDto } from '../shared/models/engagement/engagement-info-dto';
import { Messages } from '../shared/constants/messages';
import { ROUTE_URL } from '../shared/constants/route-constants';
import { SESSION_CONST } from '../shared/constants/session-constants';
import { FormGroup } from '@angular/forms';
import { UserDataService } from '../core/services/user-data.service';
import { UserDetails } from '../core/models/user-details.model';
import { ImpersonationService } from '../shared/impersonation/impersonation.service';
import { CommonService } from '../shared/common/common-service';
import { SearchStudyOption, StudySearchBy } from '../shared/constants/app-constants';
import { BaseResponse } from '../shared/models/base-response.model';
import { EngagementListDto } from '../shared/models/engagement/engagement-list-dto';
import { StudyLandingService } from '../study-home/study-landing/study-landing.service';
import { IFavoriteStudies } from './models/study.model';

@Component({
  selector: 'home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  searchStudyOption = SearchStudyOption;
  clientMasterRefId: string | null = null;
  includeArchive: boolean = false;
  externalMode: boolean = false;
  clientsByUser: ClientInfoDto[] = [];
  selectedClient: ClientInfoDto = null;
  engagementsByClientId: EngagementInfoDto[] = [];
  currentDate = new Date();
  // engagement: any = null;
  noResults: boolean = false;
  engagementNumbers: string[];
  permissions: any;
  isCreateOrEditStudyButtonHide: boolean = false;
  roleId: number;
  championRole: number = role.champion;
  adminRole: number = role.admin;
  clientAdmin: number = role.clientAdmin;
  subscriptions: Subscription[] = [];
  engagementSearchClickCount: number = 0;
  noEngagements: string;
  isClientMode: boolean;
  clientName: string = '';

  @ViewChild('engagementTypeAhead', { static: true }) engagementTypeAhead;
  @ViewChild('engagementSearch', { static: true }) engagementSearchElement: ElementRef;

  @ViewChild('clientTypeAhead', { static: true }) clientTypeAhead;
  @ViewChild('clientSearch', { static: true }) clientSearchElement: ElementRef;

  error: Error;
  switchState: boolean = false;
  userPreferenceMode: string;
  targetForm: FormGroup;
  isMainAppInstance: boolean;
  user: UserDetails;
  studySearchOptions = StudySearchBy.map(option => {
    return {
      ...option,
      isSelected: option.optionName.toLowerCase() === 'client' ? true : false
    }
  })
  searchStudyBy = 0;
  engagementAllDataFiltered: EngagementListDto[] = [];
  favoriteStudies: IFavoriteStudies[] = [];

  constructor(private _userDataService: UserDataService, private studyLandingService: StudyLandingService,
    private rightSideDrawerService: RightSideDrawerService,
    private router: Router,
    private route: ActivatedRoute,
    public sessionService: SessionService,
    public homeEditService: HomeEditAnEngagementService,
    public toastrService: ToastrService,
    private _homeService: HomeService,
    private loadingService: LoadingIndicatorService,
    public _topNavigationService: TopNavigationService,
    private window: WindowRef,
    private impersonationService: ImpersonationService,
    private commonService: CommonService
  ) {
    this.isMainAppInstance = this.window.nativeWindow === this.window.nativeWindow.parent;
  }

  ngOnInit() {
    this.getFavoriteStudies();
    this.setSearchByOption(SearchStudyOption.client);
    this.subscriptions.push(
      combineLatest([
        this.impersonationService.smeUserDetails$,
        this.impersonationService.clientManagerModeActive$
      ]).subscribe(([smeUserDetails, isClientManagerViewActive]) => {
        this.isClientMode = (!!smeUserDetails || isClientManagerViewActive);
      })
    );

    this.route.paramMap.subscribe(params => {
      this.clientMasterRefId = params.get('ClientMasterRefId');
    });

    this._topNavigationService.setStudyNav(false);
    this.subscriptions.push(this._userDataService.userDetails$.subscribe(userData => {
      if (userData) {
        this.user = userData;
        this.roleId = userData.roleId;
        this.getClients();
        this.isCreateOrEditStudyButtonHide = this.roleId === role.clientAdmin;
        this.setCCIMode();
      }
    }));

    this.sessionService.setSession('isArchived', 'false');
    this.sessionService.setSession(SESSION_CONST.ENGAGEMENTID, 0);
  }

  getFavoriteStudies(): void {
    this._homeService.getFavoriteStudies().subscribe(response => {
      if (response)
        this.favoriteStudies = response
    })
  }

  getClients() {
    this.loadingService.display(true, true);
    this._homeService.GetClientsByUser().subscribe(
      (data: ClientInfoDto[]) => {
        if (data !== null) {
          this.clientsByUser = data;
          if (this.roleId == role.clientAdmin as number) {
            this.setUpClientAdmin();
          }
          else {
            this.processPreSelectedClient();
          }
        }
        this.loadingService.display(false);
      });
  }

  processPreSelectedClient() {
    if (this.isValidNumber(this.clientMasterRefId) &&
      (this.roleId == role.admin as number || this.roleId == role.champion as number)) {
      let client = this.clientsByUser.find(client => client.masterRefId == +this.clientMasterRefId);
      if (client?.clientId > 0) {
        this.noResults = false;
        this.onClientSelected(client, true);
      }
    }
  }

  searchClientsStream = (searchPhrase$: Observable<any>) =>
    searchPhrase$.pipe(debounceTime(150), distinctUntilChanged(), tap(() => {
      this.noResults = false;
    }), switchMap((term: any) => {
      if (this.externalMode) {
        this.noResults = true;
        return of([]);
      }
      return this.searchClients(term.trim()).pipe(switchMap((data: any) => {
        this.noResults = data.length === 0;
        return of(data);
      })
      );
    })
    );

  public searchClients(term: string): Observable<any> {
    const lowerCaseTerm = term.toLowerCase();
    let searchData = this.clientsByUser;
    return of(
      searchData.filter(
        item =>
          item.clientName.toLowerCase().includes(lowerCaseTerm)
      ),
    );
  }

  clearSearch() {
    this.selectedClient = null;
    this.engagementsByClientId = [];
    this.noResults = false;
  }

  setUpClientAdmin() {
    if (this.clientsByUser?.length > 0) {
      this.onClientSelected(this.clientsByUser[0], true);
    }
    else {
      this.clearSearch();
    }
  };

  onClientSelected(client: ClientInfoDto, populateValue: boolean) {
    this.sessionService.setSession('clientId', client.clientId.toString());
    this.sessionService.setSession('clientName', client.clientName);
    this.selectedClient = {
      clientId: client.clientId as number,
      clientName: client.clientName?.toString(),
      masterRefId: client.masterRefId as number
    } as ClientInfoDto;
    if (populateValue && this.clientSearchElement?.nativeElement) {
      this.clientSearchElement.nativeElement.value = this.selectedClient.clientName;
    }
    this.getEngagements(this.selectedClient.clientId, this.includeArchive);
  }

  getEngagements(clientId: number, isArchieved: boolean) {
    this.loadingService.display(true, true);
    this._homeService.GetEngagementsByClientId(clientId, isArchieved).subscribe(
      (data: EngagementInfoDto[]) => {
        if (data !== null) {
          this.engagementsByClientId = data
        }
        this.loadingService.display(false);
      });
  }

  onboardClientClick() {
    let url = 'https://spo-global.kpmg.com/sites/GO-OI-SVC-Tax-and-Legal-Digital-Gateway/SitePages/DG-RequestCenter.aspx';
    window.open(url, '_blank');
  }

  createNewEngagementClick = () => {
    this.router.navigate(['/study-add']);
  };

  masterRefIdNull(id: number): string {
    return id == null || id == undefined ? '---' : id.toString();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
    this.rightSideDrawerService.closeRightSideDrawer();
  }

  clientFormatter = (result: any) => result.clientName;
  engagementFormatter = (result: any) => result.engagementName;

  engagementOutsideClick() {
    this.engagementSearchClickCount = 0;
    if (this.externalMode) {
      this.noResults = false;
    }
  }

  getEngagementFlag(engagementFlagId: number) {
    return getEngagementFlagById(engagementFlagId);
  }

  getEngagementFlagStyle(engagementFlagId: number) {
    return getEngagementFlagStyleById(engagementFlagId);
  }

  onViewStudyClick(engagement: EngagementInfoDto) {
    this.sessionService.setSession('engagementId', engagement.engagementId.toString());
    this.sessionService.setSession('clientId', this.selectedClient.clientId.toString());
    this.sessionService.setSession('isArchived', this.includeArchive);
    this.sessionService.setEngagementName(engagement.engagementName);
    this.sessionService.setEngagementNumber(engagement.engagementNumber);
    this.sessionService.setSession('clientName', this.selectedClient.clientName);
    this.router.navigate([ROUTE_URL.STUDYHOME(engagement.engagementId.toString())]);

    this.commonService.engagementInfo = {
      engagementId: engagement.engagementId,
      workflowId: engagement.workflowId,
      clientId: this.selectedClient.clientId,
      clientName: this.selectedClient.clientName,
      clientMasterRefId: this.selectedClient.masterRefId,
      isFavoriteStudy: engagement.isFavorite,
      libraryId: engagement.libraryId
    };
  }

  setCCIMode() {
    this.userPreferenceMode = this.sessionService.getSession(SESSION_CONST.ISCLIENTMODE);
    this.switchState = this.isClientMode === true || this.userPreferenceMode === 'true';
    this.isClientMode = this.switchState;
    if (this.isClientMode) {
      this.noEngagements = Messages.Home.EnterClientNameMessage;
    }
  }

  keydownEnterClick($event) {
    $event.preventDefault();
    if (this.externalMode) {
      const enteredClientName = ($event.target as HTMLInputElement).value;
      this.setExternalModeClient(enteredClientName);
    }
  }

  setExternalModeClient(clientName: string) {
    let client = this.clientsByUser.find(client =>
      client.clientName.toLowerCase().trim() == clientName.toLowerCase().trim());
    if (client?.clientId > 0) {
      this.noResults = false;
      this.onClientSelected(client, false);
    }
    else {
      this.noResults = true;
    }
  }

  externalModeToggleClick($event: boolean) {
    this.externalMode = $event;
  }

  includeArchiveToggleClick($event: boolean) {
    this.includeArchive = $event;
    if (this.roleId == role.clientAdmin as number) {
      this.setUpClientAdmin();
    }
  }

  isValidNumber(str): boolean {
    return str !== null && str !== undefined && !isNaN(str) && str.trim() !== '';
  }

  setSearchByOption(searchByOption: number): void {
    this.searchStudyBy = searchByOption;
    this.studySearchOptions = this.studySearchOptions.map(x => {
      return {
        ...x,
        isSelected: x.optionId === searchByOption ? true : false
      }
    })
    if (this.roleId === this.championRole && this.searchStudyBy === this.searchStudyOption.study) {
      // this.getAllEngagements();
    }
  }

  getAllEngagements(): void {
    this.loadingService.display(true, true);
    this._homeService.GetAllEngagements().subscribe((data: BaseResponse) => {
      if (data.success) {
        this.engagementAllDataFiltered = data.result;
        // this.engagementNumbers = this.engagementAllDataFiltered.map(result => result.engagementNumber);
        // this.getEngagementsByUserId();
      }
      if (data.result.length === 0) {
        this.noEngagements = Messages.Home.NoEngagements;
      }
      this.loadingService.display(false);
    });
  }

  public searchEngagements(term: string): Observable<any> {
    const lowerCaseTerm = term.toLowerCase();
    let searchData = this.engagementAllDataFiltered;
    return of(
      searchData.filter(item =>
        item.engagementName.toLowerCase().includes(lowerCaseTerm)
      ));
  }

  searchEngagementsStream = (searchPhrase$: Observable<any>) =>
    searchPhrase$.pipe(debounceTime(500), distinctUntilChanged(), tap((stdtext) => {
      this.noResults = false;
    }), switchMap((term: any) => {
      if (term && term?.length > 0 && this.externalMode) {
        return this._homeService.getEngagementBySearchText(term.trim(), this.includeArchive).pipe(switchMap((data: any) => {
          const studyIndex = data.findIndex(x => x.engagementName.toLowerCase() === term.toLowerCase().trim());
          this.noResults = data && data.length === 0 ? true : studyIndex === -1 ? true : false;
          return studyIndex > -1 && data && data[studyIndex] ? of([data[studyIndex]]) : of([]);
        }));
      }
      // Process the search term entered
      if (term && term?.length > 0 && !this.externalMode) {
        return this._homeService.getEngagementBySearchText(term.trim(), this.includeArchive).pipe(
          switchMap((data: any) => {
            this.noResults = data.length === 0;
            return data && data.length > 0 ? of(data) : of([]);
          }));
      } else {
        return of([]);
      }
    })
    );

  //Select engagement from search typehead
  onEngagementFilter(event) {
    this.sessionService.setSession('engagementId', event.item.engagementId.toString());
    this.sessionService.setSession('clientId', event.item.clientId.toString());
    this.sessionService.setEngagementName(event.item.engagementName);
    this.sessionService.setEngagementNumber(event.item.engagementNumber);
    this.sessionService.setSession('clientName', event.item.clientName);

    const engDataIndex = this.engagementAllDataFiltered.findIndex(x => x.engagementId === event.item.engagementId);
    if (engDataIndex > -1) {
      this.studyLandingService.setStudyDetails(this.engagementAllDataFiltered[engDataIndex]);
    }

    if (event.item.isSurveyExists === true) {
      this.router.navigate([ROUTE_URL.STUDYHOME(event.item.engagementId)]);
    } else {
      this.router.navigate([ROUTE_URL.STUDYHOME(event.item.engagementId)]);
    }
  }

  engagementSearchClick() {
    // this.engagementSearchClickCount++;
    // if (this.engagementSearch && this.engagementSearch.nativeElement && this.engagementSearchClickCount % 2 === 0) {
    //   this.ngxTable?.element?.focus();
    //   this.engagementSearch.nativeElement.blur();
    //   this.engagementSearchClickCount = 0;
    // }
  }

}
